import {useEffect, useMemo, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  Clock3,
  FileCheck2,
  History,
  Loader2,
  PackagePlus,
  RefreshCw,
  Search,
  SlidersHorizontal,
} from 'lucide-react'
import {
  fetchModifiedPurchaseOrderHistory,
  fetchSavedPurchaseOrder,
  fetchSavedPurchaseOrders,
  ModifiedPurchaseOrderHistory,
  SavedOrderStatus,
  SavedPurchaseOrderDetail,
  SavedPurchaseOrder,
} from '../services/purchaseOrdersApi'
import {PurchaseOrderHistoryModal} from './PurchaseOrderHistoryModal'

function formatCnpj(value: string) {
  const digits = value.replace(/\D/g, '')
  if (digits.length !== 14) return value
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`
}

function formatDate(value?: string | null) {
  if (!value) return '—'
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value))
}

function StatusBadge({status}: {status: SavedOrderStatus}) {
  if (status === 'finalized') {
    return (
      <span className='inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-100'>
        <CheckCircle2 className='h-3.5 w-3.5' />
        Pedido criado
      </span>
    )
  }
  return (
    <span className='inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 ring-1 ring-amber-100'>
      <Clock3 className='h-3.5 w-3.5' />
      Pré-pedido
    </span>
  )
}

export function OrdersPage() {
  const navigate = useNavigate()
  const [orders, setOrders] = useState<SavedPurchaseOrder[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState<'all' | SavedOrderStatus>('all')
  const [changeFilter, setChangeFilter] = useState<'all' | 'modified' | 'seller' | 'lojista' | 'unmodified'>('all')
  const [search, setSearch] = useState('')
  const [historyDetail, setHistoryDetail] = useState<SavedPurchaseOrderDetail | null>(null)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyError, setHistoryError] = useState('')
  const [showAllHistory, setShowAllHistory] = useState(false)
  const [allHistory, setAllHistory] = useState<ModifiedPurchaseOrderHistory[]>([])
  const [allHistoryLoaded, setAllHistoryLoaded] = useState(false)

  const loadOrders = async () => {
    setLoading(true)
    setError('')
    try {
      setOrders(await fetchSavedPurchaseOrders())
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Erro ao carregar pedidos.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadOrders()
  }, [])

  const summary = useMemo(() => {
    const drafts = orders.filter((order) => order.status === 'draft').length
    const finalized = orders.length - drafts
    const modified = orders.filter((order) => order.has_manual_changes).length
    return {drafts, finalized, modified}
  }, [orders])

  const openOrderHistory = async (order: SavedPurchaseOrder) => {
    setHistoryDetail(null)
    setHistoryError('')
    setHistoryLoading(true)
    try {
      setHistoryDetail(await fetchSavedPurchaseOrder(order.order_id))
    } catch (requestError) {
      setHistoryError(requestError instanceof Error ? requestError.message : 'Erro ao carregar hist\u00f3rico.')
    } finally {
      setHistoryLoading(false)
    }
  }

  const openAllModifiedHistory = async () => {
    setShowAllHistory(true)
    setHistoryError('')
    if (allHistoryLoaded) return
    setHistoryLoading(true)
    try {
      setAllHistory(await fetchModifiedPurchaseOrderHistory())
      setAllHistoryLoaded(true)
    } catch (requestError) {
      setHistoryError(requestError instanceof Error ? requestError.message : 'Erro ao carregar hist\u00f3rico consolidado.')
    } finally {
      setHistoryLoading(false)
    }
  }

  const visibleOrders = useMemo(() => {
    const query = search.toLowerCase().trim()
    return orders.filter((order) => {
      if (filter !== 'all' && order.status !== filter) return false
      if (changeFilter === 'modified' && !order.has_manual_changes) return false
      if (changeFilter === 'seller' && !order.has_seller_changes) return false
      if (changeFilter === 'lojista' && !order.has_retailer_changes) return false
      if (changeFilter === 'unmodified' && order.has_manual_changes) return false
      if (!query) return true
      return [order.order_number, order.cnpj, order.razao_social || '']
        .join(' ')
        .toLowerCase()
        .includes(query)
    })
  }, [changeFilter, filter, orders, search])

  return (
    <div className='space-y-7'>
      <div className='flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between'>
        <div>
          <div className='mb-2 flex items-center gap-2 text-sm font-medium text-teal-700'>
            <ClipboardList className='h-4 w-4' />
            Central de pedidos
          </div>
          <h1 className='text-3xl font-bold tracking-tight text-gray-900'>Pré-pedidos e pedidos</h1>
          <p className='mt-2 text-gray-600'>Revise sugestões, acompanhe alterações e consulte pedidos finalizados.</p>
        </div>
        <div className='flex items-center gap-3'>
          <button
            type='button'
            onClick={openAllModifiedHistory}
            className='inline-flex items-center gap-2 rounded-lg border border-violet-200 bg-white px-4 py-2.5 text-sm font-semibold text-violet-700 shadow-sm transition hover:bg-violet-50'
          >
            <History className='h-4 w-4' />
            Ver todos os alterados
          </button>
          <button
            type='button'
            onClick={loadOrders}
            disabled={loading}
            className='inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:opacity-50'
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
          <button
            type='button'
            onClick={() => navigate('/lojistas/pedido-compra')}
            className='inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-700'
          >
            <PackagePlus className='h-4 w-4' />
            Criar novo pré-pedido
          </button>
        </div>
      </div>

      <div className='grid gap-4 md:grid-cols-3'>
        <div className='rounded-2xl border border-amber-100 bg-gradient-to-br from-amber-50 to-white p-5 shadow-sm'>
          <div className='flex items-start justify-between'>
            <div>
              <p className='text-sm font-medium text-amber-800'>Em revisão</p>
              <p className='mt-2 text-3xl font-bold text-gray-900'>{summary.drafts}</p>
              <p className='mt-1 text-xs text-gray-500'>pré-pedidos editáveis</p>
            </div>
            <span className='rounded-xl bg-amber-100 p-3 text-amber-700'><Clock3 className='h-5 w-5' /></span>
          </div>
        </div>
        <div className='rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-white p-5 shadow-sm'>
          <div className='flex items-start justify-between'>
            <div>
              <p className='text-sm font-medium text-emerald-800'>Finalizados</p>
              <p className='mt-2 text-3xl font-bold text-gray-900'>{summary.finalized}</p>
              <p className='mt-1 text-xs text-gray-500'>pedidos prontos para exportar</p>
            </div>
            <span className='rounded-xl bg-emerald-100 p-3 text-emerald-700'><FileCheck2 className='h-5 w-5' /></span>
          </div>
        </div>
        <div className='rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 to-white p-5 shadow-sm'>
          <div className='flex items-start justify-between'>
            <div>
              <p className='text-sm font-medium text-blue-800'>Com ajustes</p>
              <p className='mt-2 text-3xl font-bold text-gray-900'>{summary.modified}</p>
              <p className='mt-1 text-xs text-gray-500'>diferentes da sugestão automática</p>
            </div>
            <span className='rounded-xl bg-blue-100 p-3 text-blue-700'><SlidersHorizontal className='h-5 w-5' /></span>
          </div>
        </div>
      </div>

      <div className='overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm'>
        <div className='flex flex-col gap-4 border-b border-gray-100 p-5 xl:flex-row xl:items-center xl:justify-between'>
          <div className='flex flex-wrap items-center gap-3'>
            {([
              ['all', 'Todos'],
              ['draft', 'Pré-pedidos'],
              ['finalized', 'Pedidos'],
            ] as const).map(([value, label]) => (
              <button
                key={value}
                type='button'
                onClick={() => setFilter(value)}
                className={`rounded-md px-3.5 py-2 text-sm font-semibold transition ${
                  filter === value ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {label}
              </button>
            ))}
            <select
              value={changeFilter}
              onChange={(event) => setChangeFilter(event.target.value as typeof changeFilter)}
              className='rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm font-semibold text-gray-700 outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100'
            >
              <option value='all'>Todas as alterações</option>
              <option value='modified'>Com alteração manual</option>
              <option value='seller'>Alterados pelo seller</option>
              <option value='lojista'>Alterados pelo lojista</option>
              <option value='unmodified'>Sem alteração manual</option>
            </select>
          </div>
          <div className='relative w-full md:max-w-sm'>
            <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400' />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder='Buscar por número, CNPJ ou razão social...'
              className='w-full rounded-lg border border-gray-200 py-2.5 pl-10 pr-3 text-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100'
            />
          </div>
        </div>

        {loading ? (
          <div className='flex min-h-[320px] items-center justify-center'>
            <div className='text-center text-gray-500'>
              <Loader2 className='mx-auto mb-3 h-8 w-8 animate-spin text-teal-600' />
              Carregando pedidos...
            </div>
          </div>
        ) : error ? (
          <div className='flex min-h-[320px] flex-col items-center justify-center px-6 text-center'>
            <span className='mb-3 rounded-full bg-red-50 p-3 text-red-600'><AlertCircle className='h-6 w-6' /></span>
            <p className='font-semibold text-gray-900'>Não foi possível carregar os pedidos</p>
            <p className='mt-1 max-w-md text-sm text-gray-500'>{error}</p>
          </div>
        ) : visibleOrders.length === 0 ? (
          <div className='flex min-h-[320px] flex-col items-center justify-center px-6 text-center'>
            <span className='mb-4 rounded-2xl bg-gray-100 p-4 text-gray-400'><ClipboardList className='h-8 w-8' /></span>
            <p className='font-semibold text-gray-900'>Nenhum pedido encontrado</p>
            <p className='mt-1 max-w-md text-sm text-gray-500'>Crie um pré-pedido a partir da tela de Pedido de Compra para iniciar a revisão.</p>
          </div>
        ) : (
          <div className='overflow-x-auto'>
            <table className='w-full min-w-[920px]'>
              <thead className='bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500'>
                <tr>
                  <th className='px-6 py-3.5'>Pedido</th>
                  <th className='px-4 py-3.5'>Loja</th>
                  <th className='px-4 py-3.5'>Status</th>
                  <th className='px-4 py-3.5 text-right'>Itens</th>
                  <th className='px-4 py-3.5 text-right'>Unidades</th>
                  <th className='px-4 py-3.5'>Alterações</th>
                  <th className='px-4 py-3.5'>Criado em</th>
                  <th className='px-6 py-3.5' />
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-100'>
                {visibleOrders.map((order) => (
                  <tr
                    key={order.order_id}
                    onClick={() => navigate(`/lojistas/pedidos/${order.order_id}`)}
                    className='cursor-pointer transition hover:bg-gray-50/80'
                  >
                    <td className='px-6 py-4'>
                      <p className='font-semibold text-gray-900'>{order.order_number}</p>
                      <p className='mt-0.5 text-xs text-gray-500'>origem automática</p>
                    </td>
                    <td className='px-4 py-4'>
                      <p className='font-mono text-sm font-semibold text-gray-800'>{formatCnpj(order.cnpj)}</p>
                      {order.razao_social && <p className='mt-0.5 max-w-[220px] truncate text-xs text-gray-500'>{order.razao_social}</p>}
                    </td>
                    <td className='px-4 py-4'><StatusBadge status={order.status} /></td>
                    <td className='px-4 py-4 text-right text-sm font-semibold text-gray-800'>{order.item_count}</td>
                    <td className='px-4 py-4 text-right text-sm font-semibold text-gray-800'>{order.total_units}</td>
                    <td className='px-4 py-4'>
                      {order.has_manual_changes ? (
                        <span className='text-sm font-semibold text-blue-700'>{order.manual_adjustment_count} ajuste(s) · {order.modified_items} item(ns) atuais</span>
                      ) : (
                        <span className='text-sm text-gray-500'>Sem alterações</span>
                      )}
                    </td>
                    <td className='px-4 py-4 text-sm text-gray-600'>{formatDate(order.created_at)}</td>
                    <td className='px-6 py-4 text-right'>
                      <div className='flex items-center justify-end gap-2'>
                        <button
                          type='button'
                          onClick={(event) => {
                            event.stopPropagation()
                            openOrderHistory(order)
                          }}
                          className='inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-violet-700 transition hover:border-violet-200 hover:bg-violet-50'
                        >
                          <History className='h-3.5 w-3.5' /> Histórico
                        </button>
                        <ArrowRight className='h-4 w-4 text-gray-400' />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <PurchaseOrderHistoryModal
        open={!showAllHistory && (historyLoading || Boolean(historyDetail) || Boolean(historyError))}
        onClose={() => {
          setHistoryDetail(null)
          setHistoryError('')
          setHistoryLoading(false)
        }}
        loading={historyLoading && !showAllHistory}
        error={!showAllHistory ? historyError : ''}
        title={historyDetail ? `Histórico de ${historyDetail.order.order_number}` : 'Histórico do pedido'}
        groups={historyDetail ? [{
          order_id: historyDetail.order.order_id,
          order_number: historyDetail.order.order_number,
          cnpj: historyDetail.order.cnpj,
          razao_social: historyDetail.order.razao_social,
          status: historyDetail.order.status,
          generation_origin: historyDetail.order.generation_origin,
          is_modified: historyDetail.order.is_modified,
          created_at: historyDetail.order.created_at,
          updated_at: historyDetail.order.updated_at,
          changes: historyDetail.audit,
        }] : []}
      />
      <PurchaseOrderHistoryModal
        open={showAllHistory}
        onClose={() => {
          setShowAllHistory(false)
          setHistoryError('')
        }}
        loading={historyLoading}
        error={showAllHistory ? historyError : ''}
        title='Todos os pedidos alterados'
        subtitle='Visão consolidada de todas as alterações manuais realizadas nos pedidos acessíveis.'
        groups={allHistory}
      />

    </div>
  )
}
